import { Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type OrgDto,
  Regulation,
  type SourcingEventV2Dto,
} from "../../../../../../autogen/bff-api";
import { showsHomeDepartment } from "../../../../../../common/departments/departmentVisibility";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "TitlePresent",
  "HomeDepartmentPresent",
  "RegulationSet",
  "RegulationSupportsPublishing",
  "EstimatedValueSet",
  "ContractNatureSet",
  "FoaPartSet",
  "FoaPartUsesPublicFlow",
  "UtilityActivitySet",
] as const satisfies readonly KnownFieldKey[];

interface OverviewSectionProps {
  event: SourcingEventV2Dto;
  organization: OrgDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const OverviewSection = ({
  event,
  organization,
  readinessFor,
  onWizardStepNavigate,
}: OverviewSectionProps) => {
  const { t } = useTranslation();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procurement overview")}
      editTarget={{ kind: "wizard-step", step: "info" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryGroupHeader isFirst>{t("General information")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Title")}
          value={eventData.title || <EmptyValue text={t("Not provided")} />}
          status={readinessFor("TitlePresent")}
        />
        {showsHomeDepartment(organization, event.homeDepartment?.id) && (
          <SummaryField
            label={t("Department")}
            value={
              event.homeDepartment?.name ?? <EmptyValue text={t("Not set")} />
            }
            status={readinessFor("HomeDepartmentPresent")}
          />
        )}
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Value and regulation")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Governing regulation")}
          value={
            publicContext?.regulation ? (
              <>
                {t(publicContext.regulation)}
                {publicContext.regulation === Regulation.Forsyningsforskriften && (
                  <Text fontSize="xs" color="smMuted" fontWeight="normal">
                    {t("Announced as the utilities directive (2014/25/EU)")}
                  </Text>
                )}
              </>
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          span="half"
          status={readinessFor("RegulationSet")}
          extraStatuses={[readinessFor("RegulationSupportsPublishing")]}
        />
        {publicContext?.regulation === Regulation.Forsyningsforskriften && (
          <SummaryField
            label={t("Utility activity")}
            value={
              publicContext.utilityActivity
                ? t(publicContext.utilityActivity)
                : <EmptyValue text={t("Not set")} />
            }
            span="half"
            status={readinessFor("UtilityActivitySet")}
          />
        )}
        <SummaryField
          label={t("Applicable part of the regulation")}
          value={
            publicContext?.foaPart
              ? t(publicContext.foaPart)
              : <EmptyValue text={t("Not classified yet")} />
          }
          span="half"
          status={readinessFor("FoaPartSet")}
        />
        <SummaryField
          label={t("Estimated value")}
          value={
            publicContext?.estimatedValue?.value != null
              ? `${publicContext.estimatedValue.value.toLocaleString("nb-NO")} ${publicContext.estimatedValue.currency}`
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("EstimatedValueSet")}
          extraStatuses={[readinessFor("FoaPartUsesPublicFlow")]}
        />
        <SummaryField
          label={t("Contract type")}
          value={
            publicContext?.contractNature
              ? t(publicContext.contractNature)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ContractNatureSet")}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
